import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { MenuItem, MenuPackage, MenuCategory } from '@planner/types'

export function useMenuItems() {
  const [items, setItems] = useState<MenuItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isFetching, setIsFetching] = useState(false)

  const fetchItems = useCallback(async (includeCategories = true) => {
    setIsFetching(true)
    setError(null)

    try {
      const query = supabase
        .from('menu_items')
        .select(includeCategories ? `
          *,
          menu_item_categories (
            menu_category:menu_categories (*)
          )
        ` : '*')
        .order('name', { ascending: true })

      const { data, error: fetchError } = await query

      if (fetchError) {
        setError(fetchError.message)
        setLoading(false)
      } else {
        // Mapuj dane aby kategorie były w polu categories
        type MenuItemWithCategories = MenuItem & {
          menu_item_categories?: Array<{ menu_category?: MenuCategory }>
        }
        const itemsWithCategories = (data || []).map((item: MenuItemWithCategories) => {
          const categories = item.menu_item_categories?.map(
            (mic) => mic.menu_category
          ).filter((cat): cat is MenuCategory => !!cat) || []
          
          return {
            ...item,
            categories,
            // Zachowaj category dla kompatybilności wstecznej (pierwsza kategoria lub stara wartość)
            category: categories.length > 0 
              ? (categories[0] as { name: string })?.name 
              : item.category,
          }
        })
        
        setItems(itemsWithCategories)
        setLoading(false)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd')
      setLoading(false)
    } finally {
      setIsFetching(false)
    }
  }, [])

  useEffect(() => {
    fetchItems()
  }, [fetchItems])

  const deleteItem = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from('menu_items')
        .delete()
        .eq('id', id)

      if (error) {
        return { error: error.message }
      }

      // Refetch after delete
      await fetchItems()
      return { error: null }
    } catch (err: any) {
      return { error: err.message }
    }
  }, [fetchItems])

  // loading = true tylko gdy faktycznie fetchujemy i nie mamy danych
  const isLoading = isFetching && items.length === 0

  return {
    items,
    loading: isLoading,
    fetchItems,
    deleteItem,
  }
}

export function useMenuPackages() {
  const [packages, setPackages] = useState<MenuPackage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isFetching, setIsFetching] = useState(false)

  const fetchPackages = useCallback(async () => {
    setIsFetching(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('menu_packages')
        .select('*')
        .order('created_at', { ascending: false })

      if (fetchError) {
        setError(fetchError.message)
        setLoading(false)
      } else {
        setPackages(data || [])
        setLoading(false)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd')
      setLoading(false)
    } finally {
      setIsFetching(false)
    }
  }, [])

  const fetchPackageWithCategories = useCallback(async (packageId: string) => {
    try {
      const { data, error } = await supabase
        .from('menu_package_categories')
        .select(`
          *,
          menu_category:menu_categories (*)
        `)
        .eq('menu_package_id', packageId)
        .order('display_order', { ascending: true })

      if (error) throw error
      return data || []
    } catch (err: any) {
      console.error('Error fetching package categories:', err)
      return []
    }
  }, [])

  const fetchPackageItems = useCallback(async (packageCategoryIds: string[]) => {
    if (packageCategoryIds.length === 0) return []
    
    try {
      const { data, error } = await supabase
        .from('menu_package_items')
        .select(`
          *,
          menu_item:menu_items (*)
        `)
        .in('menu_package_category_id', packageCategoryIds)
        .order('display_order', { ascending: true })

      if (error) throw error
      return data || []
    } catch (err: any) {
      console.error('Error fetching package items:', err)
      return []
    }
  }, [])

  useEffect(() => {
    fetchPackages()
  }, [fetchPackages])

  const deletePackage = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from('menu_packages')
        .delete()
        .eq('id', id)

      if (error) {
        return { error: error.message }
      }

      // Refetch after delete
      await fetchPackages()
      return { error: null }
    } catch (err: any) {
      return { error: err.message }
    }
  }, [fetchPackages])

  // loading = true tylko gdy faktycznie fetchujemy i nie mamy danych
  const isLoading = isFetching && packages.length === 0

  return {
    packages,
    loading: isLoading,
    fetchPackages,
    deletePackage,
    fetchPackageWithCategories,
    fetchPackageItems,
  }
}
